import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, MessageCircle, CheckCircle2 } from "lucide-react";

const Contact = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) {
      toast.error("Zpráva musí mít alespoň 5 znaků");
      return;
    }
    setSaving(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success("Zpráva odeslána – ozveme se nejpozději do 24 h");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="flex-1 container-page py-12">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <MessageCircle className="w-7 h-7 text-sky-600" />
            <h1 className="font-display text-4xl font-extrabold text-slate-900">Napište nám</h1>
          </div>
          <p className="text-slate-600 mb-8">
            Máte dotaz, problém s objednávkou nebo nápad na zlepšení? Ozvěte se &mdash; odpovídáme nejpozději do 24 hodin.
          </p>

          {submitted ? (
            <Card className="p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">Zpráva odeslána</h2>
              <p className="text-slate-600 mb-6">Děkujeme. Náš tým se vám ozve na <strong>{form.email}</strong> nejpozději do 24 hodin.</p>
              <Button variant="outline" onClick={() => { setSubmitted(false); setForm({ ...form, subject: "", message: "" }); }} data-testid="contact-send-another">
                Odeslat další zprávu
              </Button>
            </Card>
          ) : (
            <Card className="p-7">
              <form onSubmit={submit} className="grid gap-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label>Jméno</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} data-testid="contact-name" />
                  </div>
                  <div>
                    <Label>E-mail</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <Label>Předmět <span className="text-slate-400 text-xs">(volitelné)</span></Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={200} placeholder="Např. dotaz na objednávku #1234" data-testid="contact-subject" />
                </div>
                <div>
                  <Label>Vaše zpráva</Label>
                  <Textarea rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required maxLength={4000} data-testid="contact-message" placeholder="Popište nám prosím váš dotaz…" />
                  <div className="text-xs text-slate-400 mt-1">{form.message.length} / 4000</div>
                </div>
                <Button type="submit" disabled={saving} data-testid="contact-submit">
                  <Mail className="w-4 h-4 mr-2" />
                  {saving ? "Odesílám…" : "Odeslat zprávu"}
                </Button>
              </form>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
